import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchMoods,
  saveHappinessIndex,
  resetHappinessIndex,
} from "../../API_Service/HappinessIndexSlice";
import { employeeID, companyID,UserID } from "../../Common/UIComponents";

const MOOD_IMAGES = {
  1: "/Images/Happy.png", // MoodID 1 = Happy
  3: "/Images/Neutral.png", // MoodID 3 = Neutral
  2: "/Images/Sad.png", // MoodID 2 = Sad
};

export default function HappinessIndex() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { moods, checkResult, loading, saveSuccess, error } = useSelector(
    (s) => s.happinessIndex,
  );
  const user = useSelector((s) => s.auth.user);

  const [selectedMood, setSelectedMood] = useState(null);
  const [moodType, setMoodType] = useState("Official");
  const [message, setMessage] = useState("");
  const [validationErr, setValidationErr] = useState("");

  // Role guard — only TO users allowed here
  useEffect(() => {
    if (!user || user.RoleCode?.toUpperCase() !== "TO") {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    dispatch(fetchMoods());
  }, [dispatch]);

  // After save succeeds, navigate away
  useEffect(() => {
    if (saveSuccess) {
      dispatch(resetHappinessIndex());
      navigate("/lab-management/setup-lab-utilization", { replace: true }); // TO users go to SetupUtil after submitting
    }
  }, [saveSuccess, dispatch, navigate]);

  const isStartEntry = checkResult === "0"; // true = start-of-day, false = end-of-day

  function validate() {
    if (!selectedMood) {
      setValidationErr("Please select your mood");
      return false;
    }
    // Sad + Official mood requires a reason
    if (selectedMood === 2 && moodType === "Official" && !message.trim()) {
      setValidationErr("Please provide a reason for your mood");
      return false;
    }
    setValidationErr("");
    return true;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    console.log(UserID, companyID);
    dispatch(
      saveHappinessIndex({
        EmployeeID: UserID,
        MoodID: selectedMood,
        CompanyId: companyID,
        MoodType: moodType,
        Message: message,
        Isstartentry: isStartEntry ? 1 : 0,
      }),
    );
  }

  if (!user || user.RoleCode?.toUpperCase() !== "TO") return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            Happiness Index — {isStartEntry ? "Beginning" : "End of Day"}
            <span style={styles.empName}> ({user.EmployeeName})</span>
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={styles.body}>
          {/* Mood Selection */}
          <div style={styles.moodRow}>
            {[1, 3, 2].map((moodId) => {
              const mood = moods.find((m) => m.MoodID === moodId);
              if (!mood) return null;
              return (
                <label key={moodId} style={styles.moodLabel}>
                  <input
                    type="radio"
                    name="mood"
                    value={moodId}
                    style={{ display: "none" }}
                    onChange={() => {
                      setSelectedMood(moodId);
                      setValidationErr("");
                    }}
                  />
                  <img
                    src={MOOD_IMAGES[moodId]}
                    alt={mood.MoodName}
                    style={{
                      ...styles.moodImg,
                      filter:
                        selectedMood === moodId ? "none" : "grayscale(100%)",
                      transform:
                        selectedMood === moodId ? "scale(1.1)" : "scale(1)",
                    }}
                    onClick={() => {
                      setSelectedMood(moodId);
                      setValidationErr("");
                    }}
                  />
                  <span style={styles.moodName}>{mood.MoodName}</span>
                </label>
              );
            })}
          </div>

          {/* MoodType + Reason — only shown when Sad selected */}
          {selectedMood === 2 && (
            <>
              <div style={styles.radioRow}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="moodType"
                    value="Official"
                    checked={moodType === "Official"}
                    onChange={() => setMoodType("Official")}
                  />
                  &nbsp;Official
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="moodType"
                    value="Personal"
                    checked={moodType === "Personal"}
                    onChange={() => setMoodType("Personal")}
                  />
                  &nbsp;Personal
                </label>
              </div>

              <div style={styles.reasonBlock}>
                <label style={styles.reasonLabel}>Reason</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  style={styles.textarea}
                  placeholder="Enter your reason..."
                />
              </div>
            </>
          )}

          {/* Validation error */}
          {validationErr && <p style={styles.errText}>{validationErr}</p>}

          {/* API error */}
          {error && <p style={styles.errText}>{error}</p>}

          <div style={styles.footer}>
            <button type="submit" disabled={loading} style={styles.saveBtn}>
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  card: {
    background: "#fff",
    borderRadius: 8,
    width: 520,
    maxWidth: "95vw",
    boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  header: {
    background: "#074672",
    padding: "16px 24px",
  },
  title: { color: "#fff", margin: 0, fontSize: 18 },
  empName: { fontSize: 14, fontWeight: "normal" },
  body: { padding: "24px" },
  moodRow: {
    display: "flex",
    justifyContent: "space-around",
    marginBottom: 24,
  },
  moodLabel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
  },
  moodImg: {
    width: 100,
    height: 100,
    borderRadius: "50%",
    transition: "all 0.2s ease",
    objectFit: "cover",
  },
  moodName: { marginTop: 8, fontSize: 14, color: "#444" },
  radioRow: {
    display: "flex",
    gap: 32,
    marginBottom: 16,
  },
  radioLabel: { fontSize: 15, cursor: "pointer" },
  reasonBlock: { marginBottom: 16 },
  reasonLabel: { display: "block", marginBottom: 6, fontWeight: 500 },
  textarea: {
    width: "100%",
    padding: 8,
    borderRadius: 4,
    border: "1px solid #ccc",
    fontSize: 14,
    resize: "vertical",
    boxSizing: "border-box",
  },
  errText: { color: "#e74c3c", marginBottom: 8 },
  footer: { textAlign: "right", marginTop: 16 },
  saveBtn: {
    background: "#074672",
    color: "#fff",
    border: "none",
    padding: "10px 28px",
    borderRadius: 4,
    fontSize: 15,
    cursor: "pointer",
  },
};
